import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import {
  fetchPublicEvents,
  fetchPublicLeisureVideos,
  registerChildrenForFamilyEvent,
  type PublicEventItem,
  type PublicLeisureVideo
} from "@/features/public/api";
import { readStoredMemberUser, restoreMemberAccessToken, subscribeToMemberSessionChanges } from "@/features/public/memberSession";
import { formatEventDateTimeRange } from "@/shared/events/dateRange";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

interface YouTubePlayerInstance {
  loadVideoById: (videoId: string) => void;
  destroy: () => void;
}

interface YouTubeNamespace {
  Player: new (element: HTMLElement, options: Record<string, unknown>) => YouTubePlayerInstance;
  PlayerState: {
    ENDED: number;
  };
}

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeApiPromise: Promise<YouTubeNamespace> | null = null;
const MAX_CHILDREN_PER_REGISTRATION = 6;

function loadYouTubeIframeApi() {
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youtubeApiPromise) {
    return youtubeApiPromise;
  }

  youtubeApiPromise = new Promise<YouTubeNamespace>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://www.youtube.com/iframe_api"]');
    const previousHandler = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previousHandler?.();
      if (window.YT?.Player) {
        resolve(window.YT);
      } else {
        reject(new Error("Le lecteur YouTube n'a pas pu être initialisé."));
      }
    };

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.onerror = () => reject(new Error("Le script YouTube n'a pas pu être chargé."));
      document.head.appendChild(script);
    }
  });

  return youtubeApiPromise;
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
}

function YouTubePlaylistPlayer({
  video,
  onEnded
}: {
  video: PublicLeisureVideo;
  onEnded: () => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YouTubePlayerInstance | null>(null);
  const [fallbackMode, setFallbackMode] = useState(false);

  useEffect(() => {
    if (!video || fallbackMode) {
      return;
    }

    let cancelled = false;

    void loadYouTubeIframeApi()
      .then((YT) => {
        if (cancelled || !hostRef.current) {
          return;
        }

        if (playerRef.current) {
          playerRef.current.loadVideoById(video.videoId);
          return;
        }

        playerRef.current = new YT.Player(hostRef.current, {
          videoId: video.videoId,
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: 1,
            rel: 0,
            modestbranding: 1,
            playsinline: 1
          },
          events: {
            onStateChange: (event: { data: number }) => {
              if (event.data === YT.PlayerState.ENDED) {
                onEnded();
              }
            },
            onError: () => {
              setFallbackMode(true);
            }
          }
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFallbackMode(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackMode, onEnded, video]);

  useEffect(() => {
    setFallbackMode(false);
  }, [video.id]);

  useEffect(() => {
    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, []);

  if (fallbackMode) {
    return (
      <iframe
        src={video.embedUrl}
        title={video.title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    );
  }

  return <div ref={hostRef} className="absolute inset-0 h-full w-full" />;
}

export default function CulturePage() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<PublicLeisureVideo[]>([]);
  const [cultureEvents, setCultureEvents] = useState<PublicEventItem[]>([]);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedPosterEventId, setSelectedPosterEventId] = useState<string | null>(null);
  const [memberUser, setMemberUser] = useState(() => readStoredMemberUser());
  const [memberAccessToken, setMemberAccessToken] = useState<string | null>(null);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [childrenCount, setChildrenCount] = useState(1);
  const [childrenCountInput, setChildrenCountInput] = useState("1");
  const [childNames, setChildNames] = useState<string[]>([""]);
  const [registeringChildren, setRegisteringChildren] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const loadVideos = useCallback(async () => {
    setLoadingVideos(true);
    setError(null);

    try {
      const data = await fetchPublicLeisureVideos();
      setVideos(data);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les vidéos loisirs.";
      setError(message);
    } finally {
      setLoadingVideos(false);
    }
  }, []);

  const loadCultureEvents = useCallback(async () => {
    setLoadingEvents(true);

    try {
      const data = await fetchPublicEvents({
        type: "CULTURE",
        status: "PUBLISHED",
        page: 1,
        limit: 50
      });
      setCultureEvents(data.items);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les événements Culture & Famille.";
      setError(message);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    void loadVideos();
    void loadCultureEvents();
  }, [loadCultureEvents, loadVideos]);

  useEffect(() => {
    let isMounted = true;

    const syncMemberSession = async () => {
      const nextUser = readStoredMemberUser();
      const nextToken = await restoreMemberAccessToken().catch(() => null);

      if (!isMounted) {
        return;
      }

      setMemberUser(nextUser);
      setMemberAccessToken(nextToken);
    };

    void syncMemberSession();
    const unsubscribe = subscribeToMemberSessionChanges(() => {
      void syncMemberSession();
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useRealtimeInvalidation({
    topics: ["loisirs", "events"],
    requiresAuth: false,
    onInvalidate: (payload) => {
      if (payload.topics.includes("loisirs")) {
        void loadVideos();
      }

      if (payload.topics.includes("events")) {
        void loadCultureEvents();
      }
    }
  });

  useEffect(() => {
    if (!videos.length) {
      setSelectedVideoId(null);
      return;
    }

    if (!selectedVideoId || !videos.some((video) => video.id === selectedVideoId)) {
      setSelectedVideoId(videos[0]?.id ?? null);
    }
  }, [selectedVideoId, videos]);

  const selectedVideo = useMemo(
    () => videos.find((video) => video.id === selectedVideoId) ?? videos[0] ?? null,
    [selectedVideoId, videos]
  );

  const selectedVideoIndex = useMemo(() => {
    if (!selectedVideo) {
      return -1;
    }

    return videos.findIndex((video) => video.id === selectedVideo.id);
  }, [selectedVideo, videos]);

  const handlePlayNext = useCallback(() => {
    if (!videos.length) {
      return;
    }

    const nextIndex = selectedVideoIndex >= 0 ? (selectedVideoIndex + 1) % videos.length : 0;
    setSelectedVideoId(videos[nextIndex]?.id ?? null);
  }, [selectedVideoIndex, videos]);

  const selectedEvent = useMemo(
    () => cultureEvents.find((event) => event.id === selectedEventId) ?? null,
    [cultureEvents, selectedEventId]
  );
  const selectedPosterEvent = useMemo(
    () => cultureEvents.find((event) => event.id === selectedPosterEventId) ?? null,
    [cultureEvents, selectedPosterEventId]
  );
  const remainingRegistrationSpots = selectedEvent ? Math.max(1, selectedEvent.maxParticipants - selectedEvent.participants) : 1;
  const maxChildrenForSelectedEvent = Math.min(MAX_CHILDREN_PER_REGISTRATION, remainingRegistrationSpots);

  const memberDepartment = memberUser?.departement?.trim() || null;

  useEffect(() => {
    if (!selectedEvent) {
      return;
    }

    const remainingSpots = Math.min(MAX_CHILDREN_PER_REGISTRATION, Math.max(1, selectedEvent.maxParticipants - selectedEvent.participants));
    setChildrenCount((current) => {
      const nextCount = Math.min(Math.max(current, 1), remainingSpots);
      setChildrenCountInput(String(nextCount));
      return nextCount;
    });
    setChildNames((current) => {
      const nextLength = Math.min(Math.max(current.length, 1), remainingSpots);
      return Array.from({ length: nextLength }, (_, index) => current[index] ?? "");
    });
  }, [selectedEvent]);

  const openRegistration = (eventId: string) => {
    setSelectedEventId(eventId);
    setChildrenCount(1);
    setChildrenCountInput("1");
    setChildNames([""]);
    setRegistrationError(null);
  };

  const closeRegistration = () => {
    setSelectedEventId(null);
    setChildrenCount(1);
    setChildrenCountInput("1");
    setChildNames([""]);
    setRegistrationError(null);
    setRegisteringChildren(false);
  };

  const openPosterPreview = (eventId: string) => {
    setSelectedPosterEventId(eventId);
  };

  const closePosterPreview = () => {
    setSelectedPosterEventId(null);
  };

  const updateChildrenCount = (value: number) => {
    const clampedValue = Math.max(
      1,
      Math.min(
        value || 1,
        selectedEvent ? Math.min(MAX_CHILDREN_PER_REGISTRATION, Math.max(1, selectedEvent.maxParticipants - selectedEvent.participants)) : MAX_CHILDREN_PER_REGISTRATION
      )
    );

    setChildrenCount(clampedValue);
    setChildrenCountInput(String(clampedValue));
    setChildNames((current) => Array.from({ length: clampedValue }, (_, index) => current[index] ?? ""));
  };

  const handleChildrenCountInputChange = (value: string) => {
    if (value === "") {
      setChildrenCountInput("");
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    setChildrenCountInput(value);
  };

  const commitChildrenCountInput = () => {
    if (childrenCountInput.trim() === "") {
      updateChildrenCount(1);
      return;
    }

    updateChildrenCount(Number(childrenCountInput));
  };

  const updateChildName = (index: number, value: string) => {
    setChildNames((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };

  const handleRegisterChildren = async () => {
    if (!selectedEvent) {
      return;
    }

    if (!memberAccessToken) {
      navigate("/formulaires?tab=login&redirect=%2Fculture");
      return;
    }

    if (!memberDepartment) {
      setRegistrationError("Votre direction n'est pas renseignée dans votre profil membre.");
      return;
    }

    const trimmedNames = childNames.map((name) => name.trim()).filter(Boolean);

    if (trimmedNames.length !== childrenCount) {
      setRegistrationError("Merci de renseigner le nom complet de chaque enfant.");
      return;
    }

    if (trimmedNames.length > MAX_CHILDREN_PER_REGISTRATION) {
      setRegistrationError("Vous pouvez inscrire au maximum 6 enfants par inscription.");
      return;
    }

    if (new Set(trimmedNames.map((name) => name.toLowerCase())).size !== trimmedNames.length) {
      setRegistrationError("Chaque enfant doit être saisi une seule fois.");
      return;
    }

    setRegisteringChildren(true);
    setRegistrationError(null);

    try {
      const result = await registerChildrenForFamilyEvent(memberAccessToken, selectedEvent.id, trimmedNames);
      await loadCultureEvents();
      setToast({
        type: "success",
        message: `${result.children.length} enfant(s) inscrit(s) pour la direction ${result.parentDepartment}.`
      });
      closeRegistration();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Inscription impossible pour le moment.";
      setRegistrationError(message);
    } finally {
      setRegisteringChildren(false);
    }
  };

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timeout = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  return (
    <div className="min-h-screen bg-[#F8F7F4]">
      <Navbar />

      {toast ? (
        <div className={`fixed top-24 right-6 z-50 rounded-2xl px-5 py-3 text-sm font-medium text-white shadow-xl ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}>
          {toast.message}
        </div>
      ) : null}

      <section className="relative overflow-hidden bg-[linear-gradient(135deg,#111111_0%,#252525_50%,#E8521A_140%)] py-16 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(232,82,26,0.22),transparent_40%)]" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-medium text-white/90">
              <i className="ri-movie-2-line"></i>
              Loisirs en accès libre
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight md:text-5xl">Culture & Famille devient un grand espace vidéo loisirs</h1>
            <p className="mt-4 max-w-2xl text-lg text-white/75">
              Regardez librement les vidéos loisirs publiées par l&apos;administration, puis inscrivez vos enfants aux événements Culture & Famille quand vous êtes connecté.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
              <div className="text-3xl font-bold">{videos.length}</div>
              <div className="mt-1 text-sm text-white/70">vidéo(s) disponibles</div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
              <div className="text-3xl font-bold">{cultureEvents.length}</div>
              <div className="mt-1 text-sm text-white/70">événement(s) famille</div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
              <div className="text-3xl font-bold">{selectedVideo ? selectedVideoIndex + 1 : 0}</div>
              <div className="mt-1 text-sm text-white/70">lecture actuelle</div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">{error}</div>
        ) : null}

        <section className="mb-10 rounded-[32px] border border-gray-100 bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#1A8C7A]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#1A8C7A]">
                <i className="ri-group-line"></i>
                Culture & Famille
              </span>
              <h2 className="mt-3 text-2xl font-bold text-[#1A1A1A]">Événements pour vos enfants</h2>
              <p className="mt-2 max-w-2xl text-sm text-gray-500">
                Les événements culturels créés par l&apos;administration apparaissent ici. Une fois connecté, un parent peut inscrire ses enfants et ils seront automatiquement rattachés à sa direction.
              </p>
            </div>

            {memberUser ? (
              <div className="rounded-2xl border border-[#1A8C7A]/15 bg-[#F2FBF9] px-4 py-3 text-sm text-[#1A8C7A]">
                <div className="font-semibold">{memberUser.prenom} {memberUser.nom}</div>
                <div className="mt-1">Direction : {memberDepartment ?? "Non renseignée"}</div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/formulaires?tab=login&redirect=%2Fculture")}
                className="inline-flex items-center gap-2 rounded-full bg-[#E8521A] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16]"
              >
                <i className="ri-login-box-line"></i>
                Se connecter pour inscrire ses enfants
              </button>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
            {loadingEvents ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-64 animate-pulse rounded-[28px] bg-gray-100" />
              ))
            ) : cultureEvents.length > 0 ? (
              cultureEvents.map((event) => {
                const remainingSpots = Math.max(0, event.maxParticipants - event.participants);
                const isClosed = remainingSpots <= 0 || event.statut !== "PUBLISHED";
                const hasEventImage = Boolean(event.image);

                return (
                  <div key={event.id} className="flex h-full flex-col overflow-hidden rounded-[28px] border border-gray-100 bg-[#FCFBF8] shadow-sm">
                    <button
                      type="button"
                      onClick={() => openPosterPreview(event.id)}
                      className={`relative flex min-h-[240px] items-end overflow-hidden p-5 text-left text-white ${hasEventImage ? "bg-gray-900" : "bg-[linear-gradient(135deg,#1A8C7A_0%,#E8521A_100%)]"}`}
                    >
                      {hasEventImage ? (
                        <>
                          <img
                            src={event.image ?? undefined}
                            alt={event.titre}
                            className="absolute inset-0 h-full w-full scale-110 object-cover object-center opacity-35 blur-sm"
                          />
                          <img
                            src={event.image ?? undefined}
                            alt={event.titre}
                            className="absolute inset-0 h-full w-full object-cover object-center"
                          />
                        </>
                      ) : null}
                      <div
                        className={`absolute inset-0 ${
                          hasEventImage
                            ? "bg-[linear-gradient(180deg,rgba(12,16,20,0.15)_0%,rgba(12,16,20,0.48)_48%,rgba(12,16,20,0.8)_100%)]"
                            : "bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_35%)]"
                        }`}
                      />
                      {hasEventImage ? (
                        <div className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/90 backdrop-blur-sm">
                          <i className="ri-image-line"></i>
                          Voir l'affiche
                        </div>
                      ) : null}
                      <div className="relative">
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white/90">
                          <i className="ri-star-smile-line"></i>
                          Événement famille
                        </span>
                        <h3 className="mt-3 text-2xl font-bold">{event.titre}</h3>
                        <p className="mt-2 text-sm text-white/80">{formatEventDateTimeRange(event)}</p>
                      </div>
                    </button>

                    <div className="flex flex-1 flex-col p-5">
                      <div className="space-y-2 text-sm text-gray-600">
                        <p className="flex items-start gap-2">
                          <i className="ri-map-pin-line mt-0.5 text-[#E8521A]"></i>
                          <span>{event.lieu}</span>
                        </p>
                        <p className="line-clamp-3">{event.description}</p>
                      </div>

                      <div className="mt-4 rounded-2xl bg-white p-4">
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{event.participants}/{event.maxParticipants} enfant(s) inscrit(s)</span>
                          <span>{remainingSpots} place(s) restante(s)</span>
                        </div>
                        <div className="mt-3 h-2 rounded-full bg-gray-100">
                          <div
                            className="h-2 rounded-full bg-[#E8521A] transition-all"
                            style={{ width: `${event.maxParticipants > 0 ? Math.min(100, (event.participants / event.maxParticipants) * 100) : 0}%` }}
                          />
                        </div>
                      </div>

                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 sm:flex-nowrap">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isClosed ? "bg-gray-100 text-gray-500" : "bg-emerald-50 text-emerald-700"}`}>
                          {isClosed ? "Complet ou fermé" : "Inscriptions ouvertes"}
                        </span>
                        {memberUser ? (
                          <button
                            type="button"
                            disabled={isClosed}
                            onClick={() => openRegistration(event.id)}
                            className="inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-[#E8521A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:bg-gray-300 sm:ml-auto"
                          >
                            <i className="ri-user-add-line"></i>
                            Inscrire mes enfants
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate("/formulaires?tab=login&redirect=%2Fculture")}
                            className="inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-orange-200 bg-orange-50 px-5 py-2.5 text-sm font-semibold text-[#E8521A] transition-colors hover:bg-orange-100 sm:ml-auto"
                          >
                            <i className="ri-login-box-line"></i>
                            Se connecter
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="xl:col-span-3 rounded-[28px] border border-dashed border-gray-200 bg-[#FAFAF8] px-6 py-12 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 text-[#E8521A]">
                  <i className="ri-calendar-event-line text-3xl"></i>
                </div>
                <h3 className="mt-5 text-xl font-bold text-[#1A1A1A]">Aucun événement Culture & Famille pour le moment</h3>
                <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-500">
                  Dès que l&apos;administration publie un événement culturel, il apparaîtra ici et les parents connectés pourront y inscrire leurs enfants.
                </p>
              </div>
            )}
          </div>
        </section>

        {loadingVideos ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.7fr)_360px]">
            <div className="aspect-video animate-pulse rounded-[32px] bg-gray-200" />
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-3xl bg-gray-200" />
              ))}
            </div>
          </div>
        ) : selectedVideo ? (
          <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1.7fr)_360px]">
            <div className="space-y-6">
              <div className="overflow-hidden rounded-[32px] border border-gray-100 bg-black shadow-[0_30px_100px_rgba(17,24,39,0.12)]">
                <div className="relative aspect-video">
                  <YouTubePlaylistPlayer video={selectedVideo} onEnded={handlePlayNext} />
                </div>
              </div>

              <div className="rounded-[32px] border border-gray-100 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-[#E8521A]">{selectedVideo.provider}</span>
                      {selectedVideo.isLive ? (
                        <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-semibold text-white">Live</span>
                      ) : null}
                    </div>
                    <h2 className="mt-3 text-2xl font-bold text-[#1A1A1A]">{selectedVideo.title}</h2>
                    <p className="mt-2 text-sm text-gray-500">Publié le {formatDate(selectedVideo.createdAt)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handlePlayNext}
                    className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-5 py-3 text-sm font-semibold text-[#E8521A] transition-colors hover:bg-orange-100"
                  >
                    <i className="ri-play-list-2-line"></i>
                    Lire la suivante
                  </button>
                </div>
              </div>

              <div className="rounded-[32px] border border-gray-100 bg-white p-6 shadow-sm">
                <div className="mb-5 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-[#1A1A1A]">Autres vidéos loisirs</h3>
                    <p className="mt-1 text-sm text-gray-500">Cliquez sur une miniature pour lancer une autre vidéo en grand format.</p>
                  </div>
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-600">{videos.length} vidéos</span>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {videos.map((video) => {
                    const isActive = video.id === selectedVideo.id;

                    return (
                      <button
                        key={video.id}
                        type="button"
                        onClick={() => setSelectedVideoId(video.id)}
                        className={`overflow-hidden rounded-3xl border text-left transition-all ${
                          isActive
                            ? "border-[#E8521A] bg-[#FFF8F3] shadow-[0_18px_40px_rgba(232,82,26,0.12)]"
                            : "border-gray-100 bg-white hover:-translate-y-0.5 hover:shadow-lg"
                        }`}
                      >
                        <div className="relative aspect-video bg-gray-100">
                          <img src={video.thumbnailUrl} alt={video.title} className="h-full w-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                          <div className="absolute bottom-4 right-4 flex items-center gap-2">
                            {video.isLive ? (
                              <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-semibold text-white">Live</span>
                            ) : null}
                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#E8521A] shadow-lg">
                              <i className="ri-play-fill text-xl"></i>
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="line-clamp-2 font-semibold text-[#1A1A1A]">{video.title}</div>
                          <div className="mt-2 text-xs text-gray-500">{video.provider} · {formatDate(video.createdAt)}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <aside className="space-y-4">
              <div className="rounded-[32px] border border-gray-100 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-bold text-[#1A1A1A]">Lecture suivante</h3>
                <p className="mt-1 text-sm text-gray-500">À la fin d&apos;une vidéo, la suivante démarre automatiquement.</p>
              </div>

              <div className="space-y-4">
                {videos.map((video) => {
                  const isActive = video.id === selectedVideo.id;

                  return (
                    <button
                      key={video.id}
                      type="button"
                      onClick={() => setSelectedVideoId(video.id)}
                      className={`flex w-full gap-3 rounded-[28px] border p-3 text-left transition-all ${
                        isActive
                          ? "border-[#E8521A] bg-[#FFF8F3]"
                          : "border-gray-100 bg-white hover:border-orange-100 hover:bg-orange-50/40"
                      }`}
                    >
                      <div className="relative h-24 w-36 shrink-0 overflow-hidden rounded-2xl bg-gray-100">
                        <img src={video.thumbnailUrl} alt={video.title} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
                        <div className="absolute bottom-2 left-2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#E8521A]">
                          <i className="ri-play-fill"></i>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="line-clamp-2 text-sm font-semibold text-[#1A1A1A]">{video.title}</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] text-gray-600">{video.provider}</span>
                          {video.isLive ? (
                            <span className="rounded-full bg-red-500 px-2.5 py-1 text-[11px] font-semibold text-white">Live</span>
                          ) : null}
                        </div>
                        <div className="mt-3 text-xs text-gray-500">{formatDate(video.createdAt)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>
          </div>
        ) : (
          <div className="rounded-[32px] border border-dashed border-gray-200 bg-white px-6 py-20 text-center shadow-sm">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-orange-50 text-[#E8521A]">
              <i className="ri-movie-2-line text-4xl"></i>
            </div>
            <h2 className="mt-6 text-2xl font-bold text-[#1A1A1A]">Aucune vidéo loisirs pour le moment</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-500">
              Dès que l&apos;administration publie une vidéo YouTube ou YouTube Kids, elle apparaîtra ici automatiquement.
            </p>
          </div>
        )}
      </div>

      {selectedEvent ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0E1116]/60 px-4 py-6">
          <div className="absolute inset-0" onClick={closeRegistration}></div>
          <div className="relative z-10 w-full max-w-2xl rounded-[32px] bg-white p-6 shadow-[0_30px_80px_rgba(17,24,39,0.18)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-[#1A8C7A]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#1A8C7A]">
                  <i className="ri-team-line"></i>
                  Inscription enfants
                </span>
                <h3 className="mt-3 text-2xl font-bold text-[#1A1A1A]">{selectedEvent.titre}</h3>
                <p className="mt-2 text-sm text-gray-500">{formatEventDateTimeRange(selectedEvent)} · {selectedEvent.lieu}</p>
              </div>
              <button
                type="button"
                onClick={closeRegistration}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50/60 p-4 text-sm text-gray-700">
              <p className="font-medium text-[#1A1A1A]">Direction du parent</p>
              <p className="mt-1">Les enfants inscrits seront automatiquement rattachés à la direction <span className="font-semibold text-[#E8521A]">{memberDepartment ?? "non renseignée"}</span>.</p>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700">Nombre d&apos;enfants à inscrire</label>
              <div className="mt-2 rounded-2xl border border-gray-200 bg-white p-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => updateChildrenCount(childrenCount - 1)}
                    disabled={childrenCount <= 1}
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-orange-100 bg-orange-50 text-xl font-bold text-[#E8521A] transition-colors hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    -
                  </button>
                  <div className="flex-1 rounded-2xl bg-[#FFF7F2] px-4 py-3 text-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={childrenCountInput}
                      onChange={(event) => handleChildrenCountInputChange(event.target.value)}
                      onBlur={commitChildrenCountInput}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          commitChildrenCountInput();
                        }
                      }}
                      className="w-full bg-transparent text-center text-2xl font-bold text-[#1A1A1A] outline-none"
                      aria-label="Nombre d'enfants à inscrire"
                    />
                    <div className="text-xs text-gray-500">enfant(s)</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateChildrenCount(childrenCount + 1)}
                    disabled={childrenCount >= maxChildrenForSelectedEvent}
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-orange-100 bg-orange-50 text-xl font-bold text-[#E8521A] transition-colors hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    +
                  </button>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Places restantes pour cet événement : <span className="font-semibold text-[#E8521A]">{remainingRegistrationSpots}</span>
                  <span className="mx-2 text-gray-300">•</span>
                  Maximum par inscription : <span className="font-semibold text-[#1A8C7A]">{MAX_CHILDREN_PER_REGISTRATION}</span>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              {childNames.map((childName, index) => (
                <div key={`child-${index}`}>
                  <label className="block text-sm font-medium text-gray-700">Nom complet de l&apos;enfant {index + 1}</label>
                  <input
                    type="text"
                    value={childName}
                    onChange={(event) => updateChildName(index, event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] outline-none transition-colors focus:border-[#E8521A]"
                    placeholder="Nom complet"
                  />
                </div>
              ))}
            </div>

            {registrationError ? (
              <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{registrationError}</div>
            ) : null}

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeRegistration}
                className="rounded-full border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => void handleRegisterChildren()}
                disabled={registeringChildren}
                className="rounded-full bg-[#E8521A] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {registeringChildren ? "Inscription en cours..." : "Inscrire les enfants"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedPosterEvent?.image ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0E1116]/80 px-4 py-6">
          <div className="absolute inset-0" onClick={closePosterPreview}></div>
          <div className="relative z-10 flex w-full max-w-4xl flex-col overflow-hidden rounded-[32px] bg-white shadow-[0_30px_90px_rgba(17,24,39,0.28)]">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 sm:px-6">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1A8C7A]">Affiche de l'événement</div>
                <h3 className="mt-2 text-xl font-bold text-[#1A1A1A] sm:text-2xl">{selectedPosterEvent.titre}</h3>
                <p className="mt-1 text-sm text-gray-500">{formatEventDateTimeRange(selectedPosterEvent)} · {selectedPosterEvent.lieu}</p>
              </div>
              <button
                type="button"
                onClick={closePosterPreview}
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="max-h-[75vh] overflow-auto bg-[#F8F7F4] p-4 sm:p-6">
              <div className="overflow-hidden rounded-[28px] bg-white shadow-sm">
                <img
                  src={selectedPosterEvent.image}
                  alt={selectedPosterEvent.titre}
                  className="mx-auto max-h-[68vh] w-full object-contain bg-white"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
